import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@dropbox/dig-components/buttons';
import Banner from './banner';
import { EsignDisclosureControl } from 'signer-app/esign-disclosure/esign-disclosure-control';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { useSignerContext } from '../signer-experience';
import { useFieldsContext } from 'signer-app/signature-request/context';
import { Box } from '@dropbox/dig-foundations';
import { useBundleProps } from 'signer-app/bundle-props';
import styles from './banners.module.css';
import poweredByLogoColor from 'signer-app/white-labeling/powered-by-dropbox-sign-color.svg';
import messages from '../signer-toolbar/messages';

interface Props {
  toolbarMenu: React.ReactNode;
  esignDisclosure?: boolean;
}

const GetStartedBanner = ({ toolbarMenu, esignDisclosure }: Props) => {
  const { requiredAttachmentsLeft } = useAttachmentsInfo();
  const { requiredFieldsLeft, selectField, startEnabled } = useSignerContext();
  const { customLogoUri, hasCustomStyles } = useBundleProps();
  const intl = useIntl();
  const showPoweredByLogo = customLogoUri && !hasCustomStyles;
  const fields = useFieldsContext();

  const handleGetStarted = () => {
    const field = fields[0];
    selectField(field.id);
  };

  const actions = [];

  if (showPoweredByLogo) {
    actions.push(
      <Box display="flex" key="powered-by-logo">
        <img
          src={poweredByLogoColor}
          className={styles.poweredByLogo}
          alt={intl.formatMessage(messages.poweredByDropbox)}
        />
      </Box>,
    );
  }

  return (
    <Banner
      title={
        <FormattedMessage
          id="signer.preview.getStartedButton.labelText"
          defaultMessage="Get started"
          description="Button text in signer flow, when clicked goes to next page to fill input fields and sign"
        />
      }
      message={
        requiredAttachmentsLeft > 0 ? (
          <FormattedMessage
            id="0ce5a7c20ec56b60107c4b2cfc8963f4ec4834b1c405108a8f11fa08f162a2d5"
            defaultMessage="To complete this document, please fill in {requiredFields, plural, =1 {1 required field} other {# required fields}} and upload {requiredAttachments, plural, =1 {1 required attachment} other {# required attachments}}. Click on “Get started“ to begin."
            description="Message in signer flow, informing signer that they need to fill in the required fields to complete the document"
            values={{
              requiredFields: requiredFieldsLeft,
              requiredAttachments: requiredAttachmentsLeft,
            }}
          />
        ) : (
          <FormattedMessage
            id="3aaa3a85ae2dd16fdca4589330297ab968f94547ecd08863d64bb7ec411aa3d8"
            defaultMessage="To complete this document, please fill in {requiredFields, plural, =1 {1 required field} other {# required fields}}. Click on “Get started“ to begin."
            description="Message in signer flow, informing signer that they need to fill in the required fields to complete the document"
            values={{
              requiredFields: requiredFieldsLeft,
            }}
          />
        )
      }
      actions={[
        <Button
          variant="primary"
          size="large"
          onClick={handleGetStarted}
          key="get-started"
          disabled={!startEnabled}
        >
          <FormattedMessage
            id="signer.preview.getStartedButton.labelText"
            defaultMessage="Get started"
            description="Button text in signer flow, when clicked goes to next page to fill input fields and sign"
          />
        </Button>,
        toolbarMenu,
        ...actions,
      ]}
    >
      {esignDisclosure && <EsignDisclosureControl />}
    </Banner>
  );
};

export default GetStartedBanner;
